import { useCallback, useMemo, useState } from "react";

const DEFAULT_PAGE_SIZE = 20;

export function useServerPagination(totalItems: number, initialPageSize = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const totalPages = Math.max(1, Math.ceil(Math.max(0, totalItems) / pageSize));
  const safePage = Math.min(page, totalPages - 1);

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPage(0);
  }, []);

  const next = useCallback(() => {
    setPage((current) => Math.min(current + 1, totalPages - 1));
  }, [totalPages]);

  const prev = useCallback(() => {
    setPage((current) => Math.max(current - 1, 0));
  }, []);

  const reset = useCallback(() => {
    setPage(0);
  }, []);

  return useMemo(
    () => ({
      page: safePage,
      totalPages,
      totalItems,
      setPage,
      hasNext: safePage < totalPages - 1,
      hasPrev: safePage > 0,
      next,
      prev,
      reset,
      pageStart: totalItems > 0 ? safePage * pageSize + 1 : 0,
      pageEnd: Math.min((safePage + 1) * pageSize, totalItems),
      pageSize,
      setPageSize,
    }),
    [next, pageSize, prev, reset, safePage, setPageSize, totalItems, totalPages],
  );
}

export function useCountedServerPagination(initialPageSize = DEFAULT_PAGE_SIZE) {
  const [totalItems, setTotalItems] = useState(0);
  return {
    ...useServerPagination(totalItems, initialPageSize),
    setTotalItems,
  };
}
